import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  CampaignOutlined,
  DeleteOutline,
  EditOutlined,
  PeopleOutline,
  SendOutlined,
  AddOutlined,
  PreviewOutlined,
  CodeOutlined,
  AutoAwesomeOutlined,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import PageHeader from "../../common/PageHeader";
import {
  CAMPAIGN_TEMPLATES,
  fillCampaignPreview,
} from "./campaignTemplates";

const AUDIENCES = [
  {
    value: "marketing_opt_in",
    label: "Marketing opt-in (recommended)",
  },
  {
    value: "paid",
    label: "Paid plans",
  },
  {
    value: "free",
    label: "Free / trial",
  },
  {
    value: "all_active",
    label: "All active (marketing opt-in)",
  },
  {
    value: "signed_up_7d",
    label: "Signed up in last 7 days",
  },
  {
    value: "signed_up_30d",
    label: "Signed up in last 30 days",
  },
];

const emptyForm = {
  id: null,
  title: "",
  subject: "",
  html_body: "",
  audience: "marketing_opt_in",
  audience_filters: { created_from: "", created_to: "" },
  template_id: null,
};

const statusColor = (s) => {
  if (s === "sent") return "success";
  if (s === "sending") return "info";
  if (s === "failed") return "error";
  return "default";
};

const MarketingCampaigns = ({ lang }) => {
  const theme = useTheme();
  const { hitAxios } = React.useContext(GlobalContext);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [seeding, setSeeding] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [form, setForm] = React.useState(emptyForm);
  const [editorTab, setEditorTab] = React.useState(0);
  const [audienceCount, setAudienceCount] = React.useState(null);
  const [snack, setSnack] = React.useState({
    open: false,
    msg: "",
    severity: "success",
  });

  const showSnack = (msg, severity = "success") =>
    setSnack({ open: true, msg, severity });

  const load = React.useCallback(async () => {
    setLoading(true);
    const campRes = await hitAxios({
      path: "/api/admin/marketing/campaigns",
      post: false,
      admin: true,
      showSnackbar: false,
    });
    if (campRes?.data?.success) setRows(campRes.data.data || []);
    setLoading(false);
  }, [hitAxios]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const audience = params.get("audience");
    if (audience && AUDIENCES.some((a) => a.value === audience)) {
      setForm((p) => ({ ...p, audience }));
    }
  }, []);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const res = await hitAxios({
        path: "/api/admin/marketing/audience/preview",
        post: true,
        admin: true,
        obj: {
          audience: form.audience,
          audience_filters: form.audience_filters,
        },
        showSnackbar: false,
      });
      if (!cancelled && res?.data?.success) {
        setAudienceCount(res.data.data?.count ?? 0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, form.audience, form.audience_filters, hitAxios]);

  const previewHtml = React.useMemo(
    () =>
      fillCampaignPreview(form.html_body, {
        campaign_title: form.title || "Campaign preview",
      }),
    [form.html_body, form.title],
  );

  const applyTemplate = (tpl) => {
    setForm({
      id: null,
      title: tpl.title,
      subject: tpl.subject,
      html_body: tpl.html_body,
      audience: tpl.audience || "marketing_opt_in",
      audience_filters: { created_from: "", created_to: "" },
      template_id: tpl.id,
    });
    setEditorTab(1);
    setPickerOpen(false);
    setOpen(true);
  };

  const openCreate = () => {
    const params = new URLSearchParams(window.location.search);
    const audienceParam = params.get("audience");
    const audience =
      audienceParam && AUDIENCES.some((a) => a.value === audienceParam)
        ? audienceParam
        : "marketing_opt_in";
    setForm({ ...emptyForm, audience });
    setEditorTab(0);
    setAudienceCount(null);
    setPickerOpen(true);
  };

  const openBlank = () => {
    const first = CAMPAIGN_TEMPLATES[0];
    setForm({
      ...emptyForm,
      title: "Untitled campaign",
      subject: "",
      html_body: first?.html_body || "",
    });
    setEditorTab(0);
    setPickerOpen(false);
    setOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      title: row.title || "",
      subject: row.subject || "",
      html_body: row.html_body || "",
      audience: row.audience || "marketing_opt_in",
      audience_filters: {
        created_from: row.audience_filters?.created_from || "",
        created_to: row.audience_filters?.created_to || "",
      },
      template_id: null,
    });
    setEditorTab(1);
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim() || !form.subject.trim() || !form.html_body.trim()) {
      return showSnack("Title, subject, and body are required", "warning");
    }
    setSaving(true);
    const path = form.id
      ? "/api/admin/marketing/campaigns/update"
      : "/api/admin/marketing/campaigns";
    const res = await hitAxios({
      path,
      post: true,
      admin: true,
      obj: form,
      showSnackbar: false,
    });
    setSaving(false);
    if (res?.data?.success) {
      showSnack(res.data.msg || "Saved");
      setOpen(false);
      load();
    } else {
      showSnack(res?.data?.msg || "Save failed", "error");
    }
  };

  const seedStarters = async () => {
    if (
      !window.confirm(
        `Create ${CAMPAIGN_TEMPLATES.length} professional draft campaigns from templates? You can edit before sending.`,
      )
    ) {
      return;
    }
    setSeeding(true);
    let ok = 0;
    for (const tpl of CAMPAIGN_TEMPLATES) {
      const res = await hitAxios({
        path: "/api/admin/marketing/campaigns",
        post: true,
        admin: true,
        obj: {
          title: tpl.title,
          subject: tpl.subject,
          html_body: tpl.html_body,
          audience: tpl.audience,
        },
        showSnackbar: false,
      });
      if (res?.data?.success) ok += 1;
    }
    setSeeding(false);
    showSnack(
      ok
        ? `Seeded ${ok} draft campaign${ok === 1 ? "" : "s"}`
        : "Could not seed campaigns",
      ok ? "success" : "error",
    );
    load();
  };

  const send = async (row) => {
    if (
      !window.confirm(
        `Send “${row.title}” to ~${row.recipient_count} recipients now? This uses your SMTP settings.`,
      )
    ) {
      return;
    }
    const res = await hitAxios({
      path: "/api/admin/marketing/campaigns/send",
      post: true,
      admin: true,
      obj: { id: row.id },
      showSnackbar: false,
    });
    showSnack(
      res?.data?.msg || (res?.data?.success ? "Sending…" : "Send failed"),
      res?.data?.success ? "success" : "error",
    );
    load();
    if (res?.data?.success) {
      let n = 0;
      const t = setInterval(async () => {
        n += 1;
        await load();
        if (n >= 12) clearInterval(t);
      }, 4000);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete campaign “${row.title}”?`)) return;
    const res = await hitAxios({
      path: "/api/admin/marketing/campaigns/delete",
      post: true,
      admin: true,
      obj: { id: row.id },
      showSnackbar: false,
    });
    showSnack(
      res?.data?.msg || (res?.data?.success ? "Deleted" : "Delete failed"),
      res?.data?.success ? "success" : "error",
    );
    if (res?.data?.success) load();
  };

  return (
    <Box>
      <PageHeader
        title={lang?.emailCampaigns || "Email Campaigns"}
        subtitle="Targeted one-time blasts to marketing opt-in users"
        icon={CampaignOutlined}
        primaryAction={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={
                seeding ? (
                  <CircularProgress size={14} />
                ) : (
                  <AutoAwesomeOutlined />
                )
              }
              disabled={seeding}
              onClick={seedStarters}
            >
              Seed templates
            </Button>
            <Button
              variant="contained"
              startIcon={<AddOutlined />}
              onClick={openCreate}
            >
              New campaign
            </Button>
          </Stack>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Reach uses <strong>marketing opt-in</strong> users only. For Day 0–N sequences, use{" "}
        <strong>Marketing → Automations</strong>. Merge tags:{" "}
        <code>{"{{user_name}}"}</code>, <code>{"{{login_url}}"}</code>,{" "}
        <code>{"{{unsubscribe_url}}"}</code>, <code>{"{{date}}"}</code>.
      </Alert>

      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box p={4} display="flex" justifyContent="center">
            <CircularProgress size={28} />
          </Box>
        ) : rows.length === 0 ? (
          <Box p={5} textAlign="center">
            <Typography color="text.secondary" mb={2}>
              No campaigns yet. Seed professional drafts or start from a
              template.
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center">
              <Button
                variant="outlined"
                startIcon={<AutoAwesomeOutlined />}
                onClick={seedStarters}
                disabled={seeding}
              >
                Seed templates
              </Button>
              <Button variant="contained" onClick={openCreate}>
                Browse templates
              </Button>
            </Stack>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}
              >
                <TableCell>Campaign</TableCell>
                <TableCell>Audience</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Reach</TableCell>
                <TableCell align="right">Sent / Fail</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      {r.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.subject}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {AUDIENCES.find((a) => a.value === r.audience)?.label ||
                        r.audience}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.status}
                      color={statusColor(r.status)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">{r.recipient_count}</TableCell>
                  <TableCell align="right">
                    {r.sent_count} / {r.fail_count}
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="flex-end"
                    >
                      <Button
                        size="small"
                        startIcon={<EditOutlined />}
                        disabled={r.status === "sending"}
                        onClick={() => openEdit(r)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="primary"
                        startIcon={<SendOutlined />}
                        disabled={r.status === "sending"}
                        onClick={() => send(r)}
                      >
                        Send
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteOutline />}
                        disabled={r.status === "sending"}
                        onClick={() => remove(r)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      {/* Template picker */}
      <Dialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Choose a campaign template</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {CAMPAIGN_TEMPLATES.map((tpl) => (
              <Grid item xs={12} sm={6} key={tpl.id}>
                <Box
                  sx={{
                    p: 2,
                    height: "100%",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    transition: "border-color .15s, box-shadow .15s",
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                      boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={tpl.badge} color="primary" />
                    <Typography variant="caption" color="text.secondary">
                      {tpl.category}
                    </Typography>
                  </Stack>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {tpl.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" flex={1}>
                    {tpl.description}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "text.disabled",
                      fontStyle: "italic",
                    }}
                  >
                    Subject: {tpl.subject}
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => applyTemplate(tpl)}
                    sx={{ alignSelf: "flex-start", mt: 0.5 }}
                  >
                    Use template
                  </Button>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={openBlank}>Start from blank layout</Button>
          <Button onClick={() => setPickerOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Editor + preview */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          {form.id ? "Edit campaign" : "New campaign"}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box px={2} pt={1}>
            <Tabs
              value={editorTab}
              onChange={(_, v) => setEditorTab(v)}
              sx={{ minHeight: 42 }}
            >
              <Tab
                icon={<CodeOutlined sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Edit"
                sx={{ minHeight: 42 }}
              />
              <Tab
                icon={<PreviewOutlined sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Preview"
                sx={{ minHeight: 42 }}
              />
            </Tabs>
          </Box>

          {editorTab === 0 ? (
            <Box p={2.5}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Internal title"
                    value={form.title}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, title: e.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Audience</InputLabel>
                    <Select
                      label="Audience"
                      value={form.audience}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, audience: e.target.value }))
                      }
                    >
                      {AUDIENCES.map((a) => (
                        <MenuItem key={a.value} value={a.value}>
                          {a.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Signed up from"
                    InputLabelProps={{ shrink: true }}
                    value={form.audience_filters?.created_from || ""}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        audience_filters: {
                          ...p.audience_filters,
                          created_from: e.target.value,
                        },
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Signed up to"
                    InputLabelProps={{ shrink: true }}
                    value={form.audience_filters?.created_to || ""}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        audience_filters: {
                          ...p.audience_filters,
                          created_to: e.target.value,
                        },
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PeopleOutline fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      {audienceCount == null
                        ? "Estimating audience…"
                        : `${audienceCount} recipient(s) match this audience`}
                    </Typography>
                    {!form.id && (
                      <Button
                        size="small"
                        onClick={() => {
                          setOpen(false);
                          setPickerOpen(true);
                        }}
                      >
                        Change template
                      </Button>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Email subject"
                    value={form.subject}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, subject: e.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={14}
                    label="HTML body"
                    value={form.html_body}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, html_body: e.target.value }))
                    }
                    helperText="Email-safe HTML. Switch to Preview to see the rendered result."
                    InputProps={{
                      sx: {
                        fontFamily:
                          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        fontSize: 12.5,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box
              sx={{
                bgcolor: "#e2e8f0",
                p: { xs: 1.5, sm: 3 },
                minHeight: 480,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={1.5}
                textAlign="center"
              >
                Preview with sample merge data · Subject:{" "}
                <strong>{form.subject || "(no subject)"}</strong>
              </Typography>
              <Box
                sx={{
                  maxWidth: 600,
                  mx: "auto",
                  bgcolor: "#fff",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid #cbd5e1",
                  boxShadow: "0 8px 30px rgba(15,23,42,0.08)",
                }}
              >
                <iframe
                  title="Campaign preview"
                  srcDoc={previewHtml}
                  sandbox=""
                  style={{
                    width: "100%",
                    height: 520,
                    border: 0,
                    display: "block",
                    background: "#f1f5f9",
                  }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="outlined"
            startIcon={<PreviewOutlined />}
            onClick={() => setEditorTab(1)}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={save}
            startIcon={saving ? <CircularProgress size={14} /> : null}
          >
            Save draft
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MarketingCampaigns;
